import {
  Dialog,
  Paper,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import AppBarComponent from "../../../Common/Headers/AppBarComponent";
import AceEditor from "react-ace";
import MediaLibraryModal from "../../../Common/MediaLibrary/MediaLibraryModal";
import useSignableDocumentsFullScreenEditorStyles from "./hooks/useSignableDocumentsFullScreenEditorStyles";
import { ViewMode } from "./utils/constants";
import SignableDocumentsFullScreenEditorTabs from "./components/SignableDocumentsFullScreenEditorTabs";
import { setupSignableDocumentsMappableFieldsHighlighting } from "../MessageTemplates/utils/aceEditorUtils";
import { sanitizeHTML } from "../../../../utils/htmlHelpers";
import { OPTIONS } from "../../../../utils/htmlEditorConfig";

const SignableDocumentsFullScreenEditor = ({
  open,
  onClose,
  value,
  onChange,
  onSave,
  resources,
  dealerGroupId,
  markPreviewAsViewed,
  showPreview,
  documentContext,
}) => {
  const classes = useSignableDocumentsFullScreenEditorStyles();
  const editorRef = useRef(null);
  const highlighterRef = useRef(null);
  const [viewMode, setViewMode] = useState(ViewMode.EDIT);
  const [mediaLibraryOpen, setMediaLibraryOpen] = useState(false);

  const handleEditorLoad = (editor) => {
    if (!open) {
      return;
    }

    const textarea = editor.textInput.getElement();
    if (textarea) {
      textarea.setAttribute("name", "full-screen-html-editor");
      textarea.setAttribute("id", "full-screen-html-editor");
    }

    if (highlighterRef.current) {
      highlighterRef.current.dispose();
      highlighterRef.current = null;
    }

    highlighterRef.current = setupSignableDocumentsMappableFieldsHighlighting(
      editor,
      documentContext
    );
  };

  useEffect(() => {
    if (!open && highlighterRef.current) {
      highlighterRef.current.dispose();
      highlighterRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (showPreview && viewMode !== ViewMode.PREVIEW) {
      setViewMode(ViewMode.PREVIEW);
      markPreviewAsViewed();
    }
  }, [showPreview, viewMode, markPreviewAsViewed]);

  const handleTabChange = (event, newValue) => {
    setViewMode(newValue);

    if (newValue === ViewMode.PREVIEW) {
      markPreviewAsViewed();
    }
  };

  const handleEditorChange = (newValue) => {
    onChange(newValue);
  };

  const handleSave = () => {
    onSave();
    onClose();
  };

  const handleOpenMediaLibrary = () => {
    setMediaLibraryOpen(true);
  };

  const handleCloseMediaLibrary = () => {
    setMediaLibraryOpen(false);
  };

  function hasHTMLContent() {
    return sanitizeHTML(value).replace(/<\/?(html|head|body)[^>]*>/gi, "").replace(/\s+/g, "");
  }

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <AppBarComponent
        idName="fullScreenEditor"
        title={resources["FullScreenHTMLEditor"]}
        resources={resources}
        handleClose={onClose}
        handleSubmit={handleSave}
        rightButtonResource={resources["CloseFullScreenEditor"]}
      />

      <div className={classes.content}>
        <SignableDocumentsFullScreenEditorTabs
          resources={resources}
          viewMode={viewMode}
          handleTabChange={handleTabChange}
          handleOpenMediaLibrary={handleOpenMediaLibrary}
        />

        {viewMode === ViewMode.EDIT ? (
          <div className={classes.editor}> 
            <AceEditor
              ref={editorRef}
              mode="html"
              theme="textmate"
              onChange={handleEditorChange}
              onLoad={handleEditorLoad}
              value={value || ""}
              name="full-screen-html-editor-container"
              editorProps={{ $blockScrolling: true }}
              setOptions={OPTIONS}
              width="100%"
              height="100%"
              fontSize={16}
              showPrintMargin={false}
            />
          </div>
        ) : (
          <Paper className={classes.previewContainer}>
            <iframe
              srcDoc={ hasHTMLContent() ? sanitizeHTML(value) : `<p>${resources["NoContentToPreview"]}</p>` }
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="HTML Preview"
            />
          </Paper>
        )}
      </div>

      {mediaLibraryOpen && (
        <MediaLibraryModal
          open={mediaLibraryOpen}
          onClose={handleCloseMediaLibrary}
          module="signableDocuments"
          resources={resources}
          dealerGroupId={dealerGroupId}
        />
      )}
    </Dialog>
  );
};

export default SignableDocumentsFullScreenEditor;
